import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import InstallPWA from "./components/InstallPWA";
import OfflineIndicator from "./components/OfflineIndicator";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Lazy-loaded pages for better initial bundle size
// Login page loaded eagerly as it's the entry point
import LoginPage from "./pages/LoginPage";

// Heavy pages loaded lazily
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DeviceDetail = lazy(() => import("./pages/DeviceDetail"));
const DeviceManagement = lazy(() => import("./pages/DeviceManagement"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const DeviceTypeManagement = lazy(() => import("./pages/DeviceTypeManagement"));
const AuditLogsPage = lazy(() => import("./pages/AuditLogs"));

// P3 Feature pages
const TopologyPage = lazy(() => import("./pages/Topology"));
const DriftDetectionPage = lazy(() => import("./pages/DriftDetection"));
const RegionsPage = lazy(() => import("./pages/Regions"));
const WebhooksPage = lazy(() => import("./pages/Webhooks"));

// SNMP Feature pages
const SNMPDashboard = lazy(() => import("./pages/snmp/SNMPDashboard"));
const TrapMonitoring = lazy(() => import("./pages/snmp/TrapMonitoring"));
const ThresholdManagement = lazy(() => import("./pages/snmp/ThresholdManagement"));
const AlertDashboard = lazy(() => import("./pages/snmp/AlertDashboard"));
const OIDLibrary = lazy(() => import("./pages/snmp/OIDLibrary"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Memuat halaman...</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Request timeout configuration
const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

export const axiosInstance = axios.create({
  baseURL: API,
  timeout: REQUEST_TIMEOUT,
});

// Helper function to delay retry
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Add auth token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Track retry count
  config.__retryCount = config.__retryCount || 0;
  return config;
});

// Handle errors with retry logic
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Handle auth errors - no retry
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Don't retry if request was cancelled
    if (axios.isCancel(error) || error.name === 'AbortError') {
      return Promise.reject(error);
    }

    // Retry logic for network errors or 5xx server errors
    const shouldRetry = (
      config &&
      config.__retryCount < MAX_RETRIES &&
      (
        !error.response || // Network error
        error.code === 'ECONNABORTED' || // Timeout
        (error.response.status >= 500 && error.response.status < 600) // Server error
      )
    );

    if (shouldRetry) {
      config.__retryCount += 1;
      console.log(`[Axios] Retrying request (${config.__retryCount}/${MAX_RETRIES}): ${config.url}`);

      await delay(RETRY_DELAY * config.__retryCount); // Exponential backoff
      return axiosInstance(config);
    }

    return Promise.reject(error);
  }
);

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
};

function App() {
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoChecked, setSsoChecked] = useState(false);

  // Unregister service worker (disabled untuk fix cache issue)
  useEffect(() => {
    serviceWorkerRegistration.unregister();
    console.log('PWA: Service worker disabled');
  }, []);

  // Handle SSO token from URL - MUST run before routing
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ssoToken = urlParams.get('sso_token');

    console.log('🔍 Checking for SSO token in URL...');
    console.log('🔍 Current URL:', window.location.href);
    console.log('🔍 SSO Token found:', ssoToken ? 'YES' : 'NO');
    console.log('🔍 Existing token in localStorage:', localStorage.getItem('token') ? 'YES' : 'NO');

    if (ssoToken) {
      console.log('🔐 SSO token detected, attempting auto-login...');
      setSsoLoading(true);
      
      // Always try SSO login when token is present, even if there's an old token
      // Clear any existing token first to ensure fresh login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      axios.post(`${API}/auth/sso-login`, { sso_token: ssoToken })
        .then(response => {
          console.log('✅ SSO login successful!', response.data);
          const { access_token, user } = response.data;
          localStorage.setItem('token', access_token);
          localStorage.setItem('user', JSON.stringify(user));
          
          // Remove sso_token from URL
          const newUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          
          console.log('✅ Redirecting to dashboard...');
          setSsoLoading(false);
          setSsoChecked(true);
          // Force reload to dashboard with new token
          window.location.href = '/';
        })
        .catch(error => {
          console.error('❌ SSO login failed:', error);
          console.error('❌ Error details:', error.response?.data);
          setSsoLoading(false);
          setSsoChecked(true);
          // Redirect to login page on error
          window.location.href = '/login';
        });
    } else {
      console.log('ℹ️ No SSO token in URL, normal login flow');
      // No SSO token, allow normal routing
      setSsoChecked(true);
    }
  }, []);

  // Show loading while processing SSO token
  if (!ssoChecked || ssoLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {ssoLoading ? 'Authenticating via SSO...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <OfflineIndicator />
      <InstallPWA />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/devices"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/device/:deviceId"
              element={
                <PrivateRoute>
                  <DeviceDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/device/:deviceId/manage"
              element={
                <PrivateRoute>
                  <DeviceManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/users"
              element={
                <PrivateRoute>
                  <UserManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/device-types"
              element={
                <PrivateRoute>
                  <DeviceTypeManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <PrivateRoute>
                  <AuditLogsPage />
                </PrivateRoute>
              }
            />
            {/* P3 Feature Routes */}
            <Route
              path="/topology"
              element={
                <PrivateRoute>
                  <TopologyPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/drift"
              element={
                <PrivateRoute>
                  <DriftDetectionPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/regions"
              element={
                <PrivateRoute>
                  <RegionsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/webhooks"
              element={
                <PrivateRoute>
                  <WebhooksPage />
                </PrivateRoute>
              }
            />
            {/* SNMP Feature Routes */}
            <Route
              path="/snmp"
              element={
                <PrivateRoute>
                  <SNMPDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/snmp/dashboard"
              element={
                <PrivateRoute>
                  <SNMPDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/snmp/traps"
              element={
                <PrivateRoute>
                  <TrapMonitoring />
                </PrivateRoute>
              }
            />
            <Route
              path="/snmp/thresholds"
              element={
                <PrivateRoute>
                  <ThresholdManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/snmp/alerts"
              element={
                <PrivateRoute>
                  <AlertDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/snmp/oid-library"
              element={
                <PrivateRoute>
                  <OIDLibrary />
                </PrivateRoute>
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
